/**
 * AssetsDashboard.js
 *
 * Full-featured asset management module modeled after Autodesk Build Assets.
 * Left sidebar: Assets/Systems tabs, category filter (All categories, Equipment, etc.)
 * Main area: Data table with configurable columns, search, filter, export
 * Top-right: Create asset, Deleted assets, Settings dropdown, Export, Search, Filter, Column config
 */

import Box from '@mui/material/Box';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';
import useColumnOrder from '../../../hooks/useColumnOrder';
import ColumnConfigPanel from '../../common/ColumnConfigPanel';
import { applyColumnOrder } from '../../../utils/columnLayout';

const API_BASE = '/technical-assets';

const ALL_COLUMNS = [
  { key: 'name', label: 'Name', width: 180, minWidth: 100, alwaysVisible: true },
  { key: 'category_name', label: 'Category', width: 140, minWidth: 80 },
  { key: 'status_name', label: 'Status', width: 110, minWidth: 70 },
  { key: 'description', label: 'Description', width: 220, minWidth: 100 },
  { key: 'location', label: 'Location', width: 180, minWidth: 100 },
  { key: 'barcode', label: 'Barcode', width: 140, minWidth: 80 },
  { key: 'created_at', label: 'Created on', width: 130, minWidth: 90 },
  { key: 'updated_at', label: 'Updated on', width: 130, minWidth: 90 },
  { key: 'created_by', label: 'Created by', width: 150, minWidth: 90 },
  { key: 'updated_by', label: 'Updated by', width: 150, minWidth: 90 },
  { key: 'placement', label: 'Placement', width: 160, minWidth: 90 },
  { key: 'model_name', label: 'Models', width: 150, minWidth: 90 },
  { key: 'sea_can_number', label: 'Sea can number', width: 150, minWidth: 90 },
];

const DEFAULT_VISIBLE_KEYS = ['name', 'category_name', 'status_name', 'description', 'location', 'barcode', 'created_at'];

const SETTINGS_ITEMS = [
  { id: 'models', label: 'Models' },
  { id: 'asset-categories', label: 'Asset categories' },
  { id: 'system-categories', label: 'System categories' },
  { id: 'custom-fields', label: 'Custom fields' },
  { id: 'status-sets', label: 'Status sets' },
  { id: 'permissions', label: 'Permissions' },
];

const AssetsDashboard = ({ projectId }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('technicalAssets', projectId || 'global', true);

  // Data
  const [assets, setAssets] = useState([]);
  const [deletedAssets, setDeletedAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [models, setModels] = useState([]);
  const [systemCategories, setSystemCategories] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state
  const [activeTab, setActiveTab] = useState('assets');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [toast, setToast] = useState(null);

  // Column config
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const cols = {};
    ALL_COLUMNS.forEach(c => { cols[c.key] = DEFAULT_VISIBLE_KEYS.includes(c.key); });
    return cols;
  });
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const { columnOrder, moveColumn } = useColumnOrder('assets-column-order', ALL_COLUMNS);

  // Modals / dropdowns
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeletedView, setShowDeletedView] = useState(false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(null);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Create form
  const [createForm, setCreateForm] = useState({ name: '', category_id: '', status_id: '', description: '', location: '', barcode: '', placement: '', model_id: '', sea_can_number: '' });

  // Settings form
  const [settingsForm, setSettingsForm] = useState({ name: '', description: '', color: '#6b7280', manufacturer: '', category_id: '', field_type: 'text' });
  const [editingSettingsItem, setEditingSettingsItem] = useState(null);

  const columnConfigRef = useRef(null);
  const settingsRef = useRef(null);
  const exportRef = useRef(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ===== DATA FETCHING =====

  const fetchAssets = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedCategory) params.set('category_id', selectedCategory);
      if (search) params.set('search', search);
      const url = `${API_BASE}/project/${projectId}${params.toString() ? '?' + params.toString() : ''}`;
      const result = await authenticatedApiCall(instance, accounts, 'GET', url);
      if (result.data?.success) setAssets(result.data.data || []);
    } catch (err) {
      console.error('Error fetching assets:', err);
      showToast('Failed to load assets', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, projectId, selectedCategory, search]);

  const fetchCategories = useCallback(async () => {
    if (!projectId) return;
    try {
      const result = await authenticatedApiCall(instance, accounts, 'GET', `${API_BASE}/project/${projectId}/categories`);
      if (result.data?.success) setCategories(result.data.data || []);
    } catch (err) { console.error('Error fetching categories:', err); }
  }, [instance, accounts, projectId]);

  const fetchStatuses = useCallback(async () => {
    if (!projectId) return;
    try {
      const result = await authenticatedApiCall(instance, accounts, 'GET', `${API_BASE}/project/${projectId}/statuses`);
      if (result.data?.success) setStatuses(result.data.data || []);
    } catch (err) { console.error('Error fetching statuses:', err); }
  }, [instance, accounts, projectId]);

  const fetchModels = useCallback(async () => {
    if (!projectId) return;
    try {
      const result = await authenticatedApiCall(instance, accounts, 'GET', `${API_BASE}/project/${projectId}/models`);
      if (result.data?.success) setModels(result.data.data || []);
    } catch (err) { console.error('Error fetching models:', err); }
  }, [instance, accounts, projectId]);

  const fetchSystemCategories = useCallback(async () => {
    if (!projectId) return;
    try {
      const result = await authenticatedApiCall(instance, accounts, 'GET', `${API_BASE}/project/${projectId}/system-categories`);
      if (result.data?.success) setSystemCategories(result.data.data || []);
    } catch (err) { console.error('Error fetching system categories:', err); }
  }, [instance, accounts, projectId]);

  const fetchCustomFields = useCallback(async () => {
    if (!projectId) return;
    try {
      const result = await authenticatedApiCall(instance, accounts, 'GET', `${API_BASE}/project/${projectId}/custom-fields`);
      if (result.data?.success) setCustomFields(result.data.data || []);
    } catch (err) { console.error('Error fetching custom fields:', err); }
  }, [instance, accounts, projectId]);

  const fetchDeleted = useCallback(async () => {
    if (!projectId) return;
    try {
      const result = await authenticatedApiCall(instance, accounts, 'GET', `${API_BASE}/project/${projectId}/deleted`);
      if (result.data?.success) setDeletedAssets(result.data.data || []);
    } catch (err) { console.error('Error fetching deleted assets:', err); }
  }, [instance, accounts, projectId]);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);
  useEffect(() => {
    fetchCategories();
    fetchStatuses();
    fetchModels();
    fetchSystemCategories();
    fetchCustomFields();
  }, [fetchCategories, fetchStatuses, fetchModels, fetchSystemCategories, fetchCustomFields]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (columnConfigRef.current && !columnConfigRef.current.contains(e.target)) setShowColumnConfig(false);
      if (settingsRef.current && !settingsRef.current.contains(e.target)) setShowSettingsDropdown(false);
      if (exportRef.current && !exportRef.current.contains(e.target)) setShowExportDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ===== SORTING & FILTERING =====

  const displayedColumns = useMemo(() => applyColumnOrder(ALL_COLUMNS, columnOrder).filter(c => visibleColumns[c.key]), [visibleColumns, columnOrder]);

  const sortedAssets = useMemo(() => {
    const list = [...assets];
    list.sort((a, b) => {
      let va = a[sortField] ?? '';
      let vb = b[sortField] ?? '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [assets, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // ===== CRUD =====

  const handleCreate = async () => {
    if (!createForm.name.trim()) { showToast('Asset name is required', 'error'); return; }
    try {
      const payload = { ...createForm };
      if (payload.category_id === '') payload.category_id = null;
      if (payload.status_id === '') payload.status_id = null;
      if (payload.model_id === '') payload.model_id = null;
      await authenticatedApiCall(instance, accounts, 'POST', `${API_BASE}/project/${projectId}`, payload);
      setShowCreateModal(false);
      setCreateForm({ name: '', category_id: '', status_id: '', description: '', location: '', barcode: '', placement: '', model_id: '', sea_can_number: '' });
      fetchAssets();
      showToast('Asset created');
    } catch (err) {
      console.error('Create asset error:', err);
      showToast('Failed to create asset', 'error');
    }
  };

  const startEdit = (asset) => {
    setEditingAsset(asset.id);
    setEditForm({
      name: asset.name || '',
      category_id: asset.category_id || '',
      status_id: asset.status_id || '',
      description: asset.description || '',
      location: asset.location || '',
      barcode: asset.barcode || '',
      placement: asset.placement || '',
      model_id: asset.model_id || '',
      sea_can_number: asset.sea_can_number || '',
    });
  };

  const handleSaveEdit = async () => {
    try {
      const payload = { ...editForm };
      if (payload.category_id === '') payload.category_id = null;
      if (payload.status_id === '') payload.status_id = null;
      if (payload.model_id === '') payload.model_id = null;
      await authenticatedApiCall(instance, accounts, 'PUT', `${API_BASE}/asset/${editingAsset}`, payload);
      setEditingAsset(null);
      fetchAssets();
      showToast('Asset updated');
    } catch (err) {
      showToast('Failed to update asset', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this asset?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${API_BASE}/asset/${id}`);
      fetchAssets();
      showToast('Asset deleted');
    } catch (err) {
      showToast('Failed to delete asset', 'error');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedRows.size} selected assets?`)) return;
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `${API_BASE}/project/${projectId}/batch-delete`, { ids: Array.from(selectedRows) });
      setSelectedRows(new Set());
      fetchAssets();
      showToast(`Deleted ${selectedRows.size} assets`);
    } catch (err) {
      showToast('Failed to delete', 'error');
    }
  };

  const handleRestore = async (id) => {
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `${API_BASE}/asset/${id}/restore`);
      fetchDeleted();
      fetchAssets();
      showToast('Asset restored');
    } catch (err) {
      showToast('Failed to restore asset', 'error');
    }
  };

  const handleExport = async () => {
    setShowExportDropdown(false);
    try {
      const result = await authenticatedApiCall(instance, accounts, 'GET', `${API_BASE}/project/${projectId}/export`, null, { responseType: 'blob' });
      const blob = new Blob([result.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assets_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('Exported to Excel');
    } catch (err) {
      showToast('Failed to export', 'error');
    }
  };

  // ===== SETTINGS CRUD =====

  const getSettingsEndpoint = (type) => {
    const map = {
      'models': 'models', 'asset-categories': 'categories',
      'system-categories': 'system-categories', 'custom-fields': 'custom-fields',
      'status-sets': 'statuses',
    };
    return map[type] || type;
  };

  const getSettingsData = (type) => {
    const map = {
      'models': models, 'asset-categories': categories,
      'system-categories': systemCategories, 'custom-fields': customFields,
      'status-sets': statuses,
    };
    return map[type] || [];
  };

  const getSettingsRefetch = (type) => {
    const map = {
      'models': fetchModels, 'asset-categories': fetchCategories,
      'system-categories': fetchSystemCategories, 'custom-fields': fetchCustomFields,
      'status-sets': fetchStatuses,
    };
    return map[type] || (() => {});
  };

  const handleSettingsCreate = async (type) => {
    const endpoint = getSettingsEndpoint(type);
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `${API_BASE}/project/${projectId}/${endpoint}`, settingsForm);
      setSettingsForm({ name: '', description: '', color: '#6b7280', manufacturer: '', category_id: '', field_type: 'text' });
      getSettingsRefetch(type)();
      showToast(`${SETTINGS_ITEMS.find(s => s.id === type)?.label || 'Item'} created`);
    } catch (err) {
      showToast('Failed to create', 'error');
    }
  };

  const handleSettingsUpdate = async (type, id) => {
    const endpoint = getSettingsEndpoint(type);
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `${API_BASE}/${endpoint}/${id}`, settingsForm);
      setEditingSettingsItem(null);
      setSettingsForm({ name: '', description: '', color: '#6b7280', manufacturer: '', category_id: '', field_type: 'text' });
      getSettingsRefetch(type)();
      showToast('Updated');
    } catch (err) {
      showToast('Failed to update', 'error');
    }
  };

  const handleSettingsDelete = async (type, id) => {
    if (!window.confirm('Delete this item?')) return;
    const endpoint = getSettingsEndpoint(type);
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `${API_BASE}/${endpoint}/${id}`);
      getSettingsRefetch(type)();
      showToast('Deleted');
    } catch (err) {
      showToast('Failed to delete', 'error');
    }
  };

  // ===== SELECTION =====

  const toggleRow = (id) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedRows.size === sortedAssets.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(sortedAssets.map(a => a.id)));
    }
  };

  // ===== CELL RENDERER =====

  const renderCell = (asset, col) => {
    if (editingAsset === asset.id) {
      const editableFields = ['name', 'description', 'location', 'barcode', 'placement', 'sea_can_number'];
      if (editableFields.includes(col.key)) {
        return (
          <input
            type="text"
            value={editForm[col.key] || ''}
            onChange={e => setEditForm(f => ({ ...f, [col.key]: e.target.value }))}
            style={{ width: '100%', padding: '2px 6px', border: '1px solid #2196F3', borderRadius: 3, fontSize: 13, outline: 'none' }}
            autoFocus={col.key === 'name'}
          />
        );
      }
      if (col.key === 'category_name') {
        return (
          <select value={editForm.category_id || ''} onChange={e => setEditForm(f => ({ ...f, category_id: e.target.value }))} style={selectStyle}>
            <option value="">—</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        );
      }
      if (col.key === 'status_name') {
        return (
          <select value={editForm.status_id || ''} onChange={e => setEditForm(f => ({ ...f, status_id: e.target.value }))} style={selectStyle}>
            <option value="">—</option>
            {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        );
      }
      if (col.key === 'model_name') {
        return (
          <select value={editForm.model_id || ''} onChange={e => setEditForm(f => ({ ...f, model_id: e.target.value }))} style={selectStyle}>
            <option value="">—</option>
            {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        );
      }
    }

    const val = asset[col.key];
    if (col.key === 'status_name') {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 3, height: 16, borderRadius: 2, backgroundColor: asset.status_color || '#6b7280', display: 'inline-block' }} />
          {val || 'None'}
        </span>
      );
    }
    if (col.key === 'created_at' || col.key === 'updated_at') {
      if (!val) return '';
      const d = new Date(val);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    if (typeof val === 'string' && val.length > 40) return val.slice(0, 38) + '…';
    return val || '';
  };

  // ===== RENDER =====

  const selectStyle = { width: '100%', padding: '2px 4px', border: '1px solid #2196F3', borderRadius: 3, fontSize: 13, outline: 'none' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Header */}
      <div style={{ padding: '20px 24px 0', borderBottom: '1px solid #e5e7eb' }}>
        <h1 style={{ margin: '0 0 16px', fontSize: 24, fontWeight: 600, color: '#111' }}>Assets</h1>
        <div style={{ display: 'flex', gap: 0 }}>
          <button
            onClick={() => { setActiveTab('assets'); setShowDeletedView(false); }}
            style={{
              padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500,
              color: activeTab === 'assets' ? '#2196F3' : '#6b7280',
              borderBottom: activeTab === 'assets' ? '2px solid #2196F3' : '2px solid transparent',
            }}
          >
            Assets
          </button>
          <button
            onClick={() => setActiveTab('systems')}
            style={{
              padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 500,
              color: activeTab === 'systems' ? '#2196F3' : '#6b7280',
              borderBottom: activeTab === 'systems' ? '2px solid #2196F3' : '2px solid transparent',
            }}
          >
            Systems
          </button>
        </div>
      </div>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, overflow: 'hidden', minWidth: 0 }}>
        {/* Left Sidebar - Categories.
            The 170px fixed rail left only ~154px of main area on a 390px screen, which
            squeezed the asset table down to an unusable column. Below md the rail becomes
            a horizontally scrollable strip of category chips above the table instead. */}
        <Box sx={{
          width: { xs: '100%', md: 170 },
          flexShrink: 0,
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          borderRight: { xs: 'none', md: '1px solid #e5e7eb' },
          borderBottom: { xs: '1px solid #e5e7eb', md: 'none' },
          overflowX: { xs: 'auto', md: 'visible' },
          overflowY: { xs: 'hidden', md: 'auto' },
          WebkitOverflowScrolling: 'touch',
          padding: { xs: '8px 8px', md: '12px 0' },
          gap: { xs: '6px', md: 0 },
        }}>
          <Box
            onClick={() => setSelectedCategory(null)}
            sx={{
              padding: { xs: '6px 12px', md: '6px 16px' },
              cursor: 'pointer', fontSize: 13, fontWeight: selectedCategory === null ? 600 : 400,
              color: selectedCategory === null ? '#2196F3' : '#374151',
              backgroundColor: selectedCategory === null ? '#eff6ff' : 'transparent',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              borderRadius: { xs: '999px', md: 0 },
              border: { xs: '1px solid #e5e7eb', md: 'none' },
            }}
          >
            All categories
          </Box>
          {categories.map(cat => (
            <Box
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              sx={{
                padding: { xs: '6px 12px', md: '6px 16px' },
                cursor: 'pointer', fontSize: 13, fontWeight: selectedCategory === cat.id ? 600 : 400,
                color: selectedCategory === cat.id ? '#2196F3' : '#374151',
                backgroundColor: selectedCategory === cat.id ? '#eff6ff' : 'transparent',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                borderRadius: { xs: '999px', md: 0 },
                border: { xs: '1px solid #e5e7eb', md: 'none' },
              }}
            >
              {cat.name}
            </Box>
          ))}
        </Box>

        {/* Main Content */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #e5e7eb', gap: 8, flexWrap: 'wrap' }}>
            {/* Left: Create + Bulk actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowCreateModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', backgroundColor: '#2196F3', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
              >
                <span style={{ fontSize: 16, lineHeight: 1 }}>+</span> Create asset
              </button>
              {selectedRows.size > 0 && (
                <button onClick={handleBulkDelete} style={{ padding: '7px 14px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>
                  Delete {selectedRows.size} selected
                </button>
              )}
            </div>

            {/* Right: Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
              <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
              <button
                onClick={() => { setShowDeletedView(true); fetchDeleted(); }}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'none', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 13, color: '#6b7280' }}
              >
                🗑️ Deleted assets
              </button>

              {/* Settings Dropdown */}
              <div ref={settingsRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'none', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 13, color: '#374151' }}
                >
                  ⚙️ Settings {showSettingsDropdown ? '▴' : '▾'}
                </button>
                {showSettingsDropdown && (
                  <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,.12)', minWidth: 180, zIndex: 100 }}>
                    {SETTINGS_ITEMS.map(item => (
                      <div
                        key={item.id}
                        onClick={() => { setShowSettingsDropdown(false); setShowSettingsModal(item.id); }}
                        style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: '#374151', borderBottom: '1px solid #f3f4f6' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}
                      >
                        {item.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Export */}
              <div ref={exportRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', background: 'none', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 13, color: '#374151' }}
                >
                  📤 Export all ▾
                </button>
                {showExportDropdown && (
                  <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,.12)', minWidth: 160, zIndex: 100 }}>
                    <div onClick={handleExport} style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: '#374151' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
                      Export to Excel
                    </div>
                  </div>
                )}
              </div>

              {/* Search */}
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#9ca3af' }}>🔍</span>
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ padding: '6px 10px 6px 28px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, width: 150, outline: 'none' }}
                />
              </div>

              {/* Filter placeholder */}
              <button style={{ padding: '6px 10px', background: 'none', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 14, color: '#6b7280' }}>
                🔽
              </button>

              {/* Column Config (gear icon) */}
              <div ref={columnConfigRef} style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowColumnConfig(!showColumnConfig)}
                  style={{ padding: '6px 10px', background: 'none', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', fontSize: 14, color: '#374151' }}
                >
                  ⚙
                </button>
                {showColumnConfig && (
                  <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,.12)', width: 300, zIndex: 100, maxHeight: 400, overflowY: 'auto', padding: '8px 8px' }}>
                    <ColumnConfigPanel
                      columns={applyColumnOrder(ALL_COLUMNS, columnOrder)}
                      isVisible={(key) => !!visibleColumns[key]}
                      onToggle={(key) => {
                        const col = ALL_COLUMNS.find(c => c.key === key);
                        if (col?.alwaysVisible) return;
                        setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
                      }}
                      onMove={moveColumn}
                      lockedKeys={ALL_COLUMNS.filter(c => c.alwaysVisible).map(c => c.key)}
                    />
                    <div style={{ borderTop: '1px solid #e5e7eb', padding: '8px 12px', marginTop: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#6b7280' }}>
                        <span>Density</span>
                        <button style={{ padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 3, background: '#f3f4f6', cursor: 'pointer', fontSize: 12 }}>☰</button>
                        <button style={{ padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 3, background: 'none', cursor: 'pointer', fontSize: 12 }}>▬</button>
                        <button style={{ padding: '2px 6px', border: '1px solid #d1d5db', borderRadius: 3, background: 'none', cursor: 'pointer', fontSize: 12 }}>▣</button>
                      </div>
                      <button
                        onClick={() => {
                          const cols = {};
                          ALL_COLUMNS.forEach(c => { cols[c.key] = DEFAULT_VISIBLE_KEYS.includes(c.key); });
                          setVisibleColumns(cols);
                        }}
                        style={{ marginTop: 6, padding: 0, border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: '#2196F3', fontWeight: 500 }}
                      >
                        Reset all
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Table */}
          <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ width: 40, padding: '8px 12px', textAlign: 'center' }}>
                    <input type="checkbox" checked={sortedAssets.length > 0 && selectedRows.size === sortedAssets.length} onChange={toggleAll} style={{ accentColor: '#2196F3' }} />
                  </th>
                  {displayedColumns.map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 500, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none', minWidth: col.minWidth }}
                    >
                      {col.label}
                      {sortField === col.key && <span style={{ marginLeft: 4, fontSize: 10 }}>{sortDir === 'asc' ? '↑' : '↓'}</span>}
                    </th>
                  ))}
                  <th style={{ width: 80, padding: '8px 12px' }} />
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={displayedColumns.length + 2} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading assets…</td></tr>
                ) : sortedAssets.length === 0 ? (
                  <tr><td colSpan={displayedColumns.length + 2} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No assets found. Click "Create asset" to add one.</td></tr>
                ) : sortedAssets.map(asset => (
                  <tr key={asset.id} style={{ borderBottom: '1px solid #f3f4f6' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f9fafb'} onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      <input type="checkbox" checked={selectedRows.has(asset.id)} onChange={() => toggleRow(asset.id)} style={{ accentColor: '#2196F3' }} />
                    </td>
                    {displayedColumns.map(col => (
                      <td key={col.key} style={{ padding: '8px 12px', color: '#374151', maxWidth: col.width, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {renderCell(asset, col)}
                      </td>
                    ))}
                    <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {editingAsset === asset.id ? (
                        <>
                          <button onClick={handleSaveEdit} style={{ padding: '3px 8px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>Save</button>
                          <button onClick={() => setEditingAsset(null)} style={{ padding: '3px 8px', backgroundColor: '#6b7280', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 12 }}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(asset)} style={{ padding: '3px 8px', background: 'none', border: '1px solid #d1d5db', borderRadius: 3, cursor: 'pointer', fontSize: 12, marginRight: 4, color: '#374151' }}>Edit</button>
                          <button onClick={() => handleDelete(asset.id)} style={{ padding: '3px 8px', background: 'none', border: '1px solid #fca5a5', borderRadius: 3, cursor: 'pointer', fontSize: 12, color: '#ef4444' }}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Box>

      {/* ===== MODALS ===== */}

      {/* Create Asset Modal */}
      {showCreateModal && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, width: 520 }}>
            <div style={modalHeaderStyle}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Create Asset</h3>
              <button onClick={() => setShowCreateModal(false)} style={modalCloseStyle}>✕</button>
            </div>
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <label style={labelStyle}>
                Name *
                <input type="text" value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} autoFocus />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <label style={labelStyle}>
                  Category
                  <select value={createForm.category_id} onChange={e => setCreateForm(f => ({ ...f, category_id: e.target.value }))} style={inputStyle}>
                    <option value="">Select…</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </label>
                <label style={labelStyle}>
                  Status
                  <select value={createForm.status_id} onChange={e => setCreateForm(f => ({ ...f, status_id: e.target.value }))} style={inputStyle}>
                    <option value="">Select…</option>
                    {statuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </label>
              </div>
              <label style={labelStyle}>
                Description
                <textarea value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <label style={labelStyle}>
                  Location
                  <input type="text" value={createForm.location} onChange={e => setCreateForm(f => ({ ...f, location: e.target.value }))} style={inputStyle} />
                </label>
                <label style={labelStyle}>
                  Barcode
                  <input type="text" value={createForm.barcode} onChange={e => setCreateForm(f => ({ ...f, barcode: e.target.value }))} style={inputStyle} />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <label style={labelStyle}>
                  Placement
                  <input type="text" value={createForm.placement} onChange={e => setCreateForm(f => ({ ...f, placement: e.target.value }))} style={inputStyle} />
                </label>
                <label style={labelStyle}>
                  Model
                  <select value={createForm.model_id} onChange={e => setCreateForm(f => ({ ...f, model_id: e.target.value }))} style={inputStyle}>
                    <option value="">Select…</option>
                    {models.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </label>
              </div>
              <label style={labelStyle}>
                Sea Can Number
                <input type="text" value={createForm.sea_can_number} onChange={e => setCreateForm(f => ({ ...f, sea_can_number: e.target.value }))} style={inputStyle} />
              </label>
            </div>
            <div style={modalFooterStyle}>
              <button onClick={() => setShowCreateModal(false)} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={handleCreate} style={{ padding: '8px 20px', backgroundColor: '#2196F3', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Deleted Assets Modal */}
      {showDeletedView && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, width: 700, maxHeight: '80vh' }}>
            <div style={modalHeaderStyle}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>🗑️ Deleted Assets</h3>
              <button onClick={() => setShowDeletedView(false)} style={modalCloseStyle}>✕</button>
            </div>
            <div style={{ padding: 16, overflowY: 'auto', maxHeight: 'calc(80vh - 120px)' }}>
              {deletedAssets.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#9ca3af', padding: 20 }}>No deleted assets</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Category</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Deleted</th>
                      <th style={{ ...thStyle, width: 80 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {deletedAssets.map(a => (
                      <tr key={a.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={tdStyle}>{a.name}</td>
                        <td style={tdStyle}>{a.category_name || '—'}</td>
                        <td style={tdStyle}>{a.status_name || '—'}</td>
                        <td style={tdStyle}>{a.deleted_at ? new Date(a.deleted_at).toLocaleDateString() : ''}</td>
                        <td style={tdStyle}>
                          <button onClick={() => handleRestore(a.id)} style={{ padding: '3px 10px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: 12 }}>Restore</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={modalFooterStyle}>
              <button onClick={() => setShowDeletedView(false)} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 13 }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettingsModal && showSettingsModal !== 'permissions' && (
        <SettingsModal
          type={showSettingsModal}
          title={SETTINGS_ITEMS.find(s => s.id === showSettingsModal)?.label || ''}
          data={getSettingsData(showSettingsModal)}
          categories={categories}
          form={settingsForm}
          setForm={setSettingsForm}
          editingItem={editingSettingsItem}
          onEdit={(item) => {
            setEditingSettingsItem(item.id);
            setSettingsForm({
              name: item.name || '', description: item.description || '', color: item.color || '#6b7280',
              manufacturer: item.manufacturer || '', category_id: item.category_id || '', field_type: item.field_type || 'text',
            });
          }}
          onCancelEdit={() => {
            setEditingSettingsItem(null);
            setSettingsForm({ name: '', description: '', color: '#6b7280', manufacturer: '', category_id: '', field_type: 'text' });
          }}
          onCreate={() => handleSettingsCreate(showSettingsModal)}
          onUpdate={(id) => handleSettingsUpdate(showSettingsModal, id)}
          onDelete={(id) => handleSettingsDelete(showSettingsModal, id)}
          onClose={() => {
            setShowSettingsModal(null);
            setEditingSettingsItem(null);
            setSettingsForm({ name: '', description: '', color: '#6b7280', manufacturer: '', category_id: '', field_type: 'text' });
          }}
        />
      )}

      {showSettingsModal === 'permissions' && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, width: 400 }}>
            <div style={modalHeaderStyle}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Permissions</h3>
              <button onClick={() => setShowSettingsModal(null)} style={modalCloseStyle}>✕</button>
            </div>
            <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af' }}>
              <p>Asset permissions are managed through the main permission system.</p>
              <p style={{ fontSize: 13, marginTop: 8 }}>Contact an administrator for access changes.</p>
            </div>
            <div style={modalFooterStyle}>
              <button onClick={() => setShowSettingsModal(null)} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 13 }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 20, right: 20, padding: '12px 20px', borderRadius: 6, fontSize: 14, fontWeight: 500, zIndex: 10000,
          backgroundColor: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
          color: toast.type === 'error' ? '#dc2626' : '#16a34a',
          border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
          boxShadow: '0 4px 12px rgba(0,0,0,.1)',
        }}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

// ===== Settings Modal Sub-Component =====

const SettingsModal = ({ type, title, data, categories, form, setForm, editingItem, onEdit, onCancelEdit, onCreate, onUpdate, onDelete, onClose }) => {
  const renderFormFields = () => {
    switch (type) {
      case 'models':
        return (
          <>
            <input type="text" placeholder="Model name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
            <input type="text" placeholder="Manufacturer" value={form.manufacturer} onChange={e => setForm(f => ({ ...f, manufacturer: e.target.value }))} style={inputStyle} />
            <select value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))} style={inputStyle}>
              <option value="">Category (optional)</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <textarea placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ ...inputStyle, minHeight: 50 }} />
          </>
        );
      case 'status-sets':
        return (
          <>
            <input type="text" placeholder="Status name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="color" value={form.color} onChange={e => setForm(f => ({ ...f, color: e.target.value }))} style={{ width: 36, height: 32, padding: 0, border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' }} />
              <span style={{ fontSize: 13, color: '#6b7280' }}>{form.color}</span>
            </div>
          </>
        );
      case 'custom-fields':
        return (
          <>
            <input type="text" placeholder="Field name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
            <select value={form.field_type} onChange={e => setForm(f => ({ ...f, field_type: e.target.value }))} style={inputStyle}>
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="date">Date</option>
              <option value="dropdown">Dropdown</option>
              <option value="checkbox">Checkbox</option>
            </select>
          </>
        );
      default:
        return (
          <>
            <input type="text" placeholder="Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={inputStyle} />
            <textarea placeholder="Description (optional)" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ ...inputStyle, minHeight: 50 }} />
          </>
        );
    }
  };

  const renderListItem = (item) => {
    if (type === 'status-sets') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: item.color || '#6b7280' }} />
          {item.name}
        </div>
      );
    }
    if (type === 'models') {
      return (
        <div>
          <div style={{ fontWeight: 500 }}>{item.name}</div>
          {item.manufacturer && <div style={{ fontSize: 12, color: '#9ca3af' }}>{item.manufacturer}</div>}
        </div>
      );
    }
    return item.name;
  };

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 520, maxHeight: '80vh' }}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{title}</h3>
          <button onClick={onClose} style={modalCloseStyle}>✕</button>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
            {renderFormFields()}
            <div style={{ display: 'flex', gap: 8 }}>
              {editingItem ? (
                <>
                  <button onClick={() => onUpdate(editingItem)} style={{ padding: '7px 16px', backgroundColor: '#2196F3', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>Update</button>
                  <button onClick={onCancelEdit} style={{ padding: '7px 16px', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                </>
              ) : (
                <button onClick={onCreate} style={{ padding: '7px 16px', backgroundColor: '#2196F3', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 13 }}>Add</button>
              )}
            </div>
          </div>
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, maxHeight: 300, overflowY: 'auto' }}>
            {data.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#9ca3af', padding: 12, fontSize: 13 }}>No items yet</p>
            ) : data.map(item => (
              <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 4px', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ flex: 1, fontSize: 13, color: '#374151' }}>{renderListItem(item)}</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => onEdit(item)} style={{ padding: '2px 8px', background: 'none', border: '1px solid #d1d5db', borderRadius: 3, cursor: 'pointer', fontSize: 12, color: '#374151' }}>Edit</button>
                  <button onClick={() => onDelete(item.id)} style={{ padding: '2px 8px', background: 'none', border: '1px solid #fca5a5', borderRadius: 3, cursor: 'pointer', fontSize: 12, color: '#ef4444' }}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={modalFooterStyle}>
          <button onClick={onClose} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: 13 }}>Close</button>
        </div>
      </div>
    </div>
  );
};

// ===== Shared Styles =====

const overlayStyle = {
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000,
};
const modalStyle = {
  backgroundColor: '#fff', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,.18)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
};
const modalHeaderStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #e5e7eb',
};
const modalFooterStyle = {
  display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '1px solid #e5e7eb',
};
const modalCloseStyle = {
  width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', background: 'none', cursor: 'pointer', fontSize: 16, color: '#6b7280', borderRadius: 4,
};
const inputStyle = {
  width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
const labelStyle = {
  display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, fontWeight: 500, color: '#374151',
};
const thStyle = { padding: '8px 12px', textAlign: 'left', fontWeight: 500, color: '#374151', fontSize: 13 };
const tdStyle = { padding: '8px 12px', color: '#374151', fontSize: 13 };

export default AssetsDashboard;
